import React, { useCallback } from 'react';
import { TextInput, View } from 'react-native';

import { scale } from '@components/frame';
import { font } from '@styles/fonts';
import Button from '@components/button';

import { MAX_MACHINES, MIN_MACHINES } from '@components/tenantStatus/onboardingWizard/constants';

const MAX_MACHINES_DIGITS = String(MAX_MACHINES).length;
import StepperCell from './StepperCell';
import { STEPPER_BUTTON_SIZE, STEPPER_RADIUS } from './StepperCell/constants';
import { useManageNumberStepper } from './hooks/useManageNumberStepper';
import type { NumberStepperProps } from '@components/tenantStatus/onboardingWizard/types';

const NumberStepper = ({ C, value, onChange, testIDPrefix }: NumberStepperProps) => {
  const { handleDecrement, handleIncrement } = useManageNumberStepper({ value, onChange });
  const handleManualChange = useCallback(
    (text: string) => {
      const digits = text.replace(/[^0-9]/g, '').slice(0, MAX_MACHINES_DIGITS);
      const next = digits === '' ? MIN_MACHINES : Math.max(MIN_MACHINES, parseInt(digits, 10));
      onChange(Number.isFinite(next) ? next : MIN_MACHINES);
    },
    [onChange]
  );

  const buttonSize = scale(STEPPER_BUTTON_SIZE);
  const buttonRadius = scale(STEPPER_RADIUS);

  return (
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      <Button
        name="−"
        onPress={handleDecrement}
        bg=""
        width={buttonSize}
        height={buttonSize}
        borderRadius={buttonRadius}
      />
      <StepperCell C={C}>
        <TextInput
          value={String(value)}
          onChangeText={handleManualChange}
          keyboardType="number-pad"
          inputMode="numeric"
          maxLength={MAX_MACHINES_DIGITS}
          selectTextOnFocus
          testID={testIDPrefix ? `${testIDPrefix}-value` : undefined}
          style={[
            font['Headline'],
            {
              color: C('Label/Light/Primary'),
              textAlign: 'center',
              width: '100%',
              padding: 0,
              // RN-web: suppress the native focus outline that otherwise overflows the cell
              // and overlaps the +/- buttons, blocking clicks. outlineWidth alone doesn't
              // disable it on Safari, so set outlineStyle as well.
              outlineStyle: 'none',
              outlineWidth: 0,
            },
          ]}
        />
      </StepperCell>
      <Button
        name="+"
        onPress={handleIncrement}
        bg=""
        width={buttonSize}
        height={buttonSize}
        borderRadius={buttonRadius}
      />
    </View>
  );
};

export default NumberStepper;
